'use client';

import React, { useEffect } from 'react';
import { CheckCircle2, XCircle, X, Info, Sparkles } from 'lucide-react';

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info' | 'premium';
  onClose: () => void;
  duration?: number;
}

export const Toast: React.FC<ToastProps> = ({
  message,
  type,
  onClose,
  duration = 5000,
}) => {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [message, duration, onClose]);
  
  if (!message) {
    return null;
  }
  
  return (
    <div
      className={`
        fixed top-5 right-5 z-[9999999] max-w-sm
        bg-background border border-border
        rounded-lg shadow-lg
        p-4 pr-12
        animate-in slide-in-from-top-2 fade-in duration-300
        ${
          type === 'success' ? 'border-l-4 border-l-green-500' : 
          type === 'error' ? 'border-l-4 border-l-red-500' :
          type === 'info' ? 'border-l-4 border-l-blue-500' :
          'border-l-4 border-l-primary bg-gradient-to-r from-primary/5 to-transparent'
        }
      `}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        {type === 'success' ? (
          <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
        ) : type === 'error' ? (
          <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
        ) : type === 'info' ? (
          <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
        ) : (
          <Sparkles className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
        )}
        <p className="text-sm text-foreground leading-relaxed">{message}</p>
      </div>
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Close notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

interface ToastContainerProps {
  toasts: Array<{
    id: string;
    message: string;
    type: 'success' | 'error';
  }>;
  onDismiss: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  onDismiss,
}) => {
  return (
    <>
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          style={{ top: `${1 + index * 5}rem` }}
        >
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => onDismiss(toast.id)}
          />
        </div>
      ))}
    </>
  );
};